/* eslint-disable eqeqeq */
import React, { useState, useEffect, useCallback } from "react";
import {
  Grid, Typography, Button, Divider, CircularProgress,
  Card, CardContent, Toolbar, Tooltip, Box, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton,
} from "@material-ui/core";
import { Close as CloseIcon, CheckCircle, Cancel, Refresh } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { HttpRequest } from "../../../utils/httpRequests";
import NavBar from "../../../components/NavBar/NavBar";
import CustomizedSnackbar from "../../../components/CustomizedSnackbar/CustomizedSnackbar";
import LoginMenu from "../../../components/LoginMenu/LoginMenu";
import LayoutMethods from "../../../components/Layouts/layoutMethods";

// ─────────────────────────────────────────────────────────────────────────────
// Same image URL builder as F15AllThemedAssetsView
// ─────────────────────────────────────────────────────────────────────────────
const getImageUrl = (fileUrl) =>
  `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/albums/C01ImageView?fileName=${fileUrl}`;

// ─────────────────────────────────────────────────────────────────────────────
// LazyImage — auth-token fetch + reports natural dimensions via onDimensions
// Uses IntersectionObserver so images only fetch when scrolled into view,
// preventing simultaneous requests that cause 503s on slots 11+.
// ─────────────────────────────────────────────────────────────────────────────
const LazyImage = ({ src, alt, aspectRatio, onDimensions }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = React.useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!src || !isVisible) return;
    let objectUrl = null;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        console.error("Image fetch error:", e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, isVisible]);

  const paddingTop = aspectRatio ? `${(1 / aspectRatio) * 100}%` : "75%";
  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%", paddingTop }}>
      {isLoading && (
        <Skeleton variant="rect" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
      )}
      {blobUrl && (
        <img
          src={blobUrl}
          alt={alt}
          onLoad={(e) => { if (onDimensions) onDimensions(e.target.naturalWidth, e.target.naturalHeight); }}
          style={{
            position: "absolute", top: 0, left: 0,
            width: "100%", height: "100%",
            objectFit: "contain", backgroundColor: "#a9a9a96b",
          }}
        />
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// LayoutPreview — exact match to F15ThemeProductsAssetsForm version
// ─────────────────────────────────────────────────────────────────────────────
const LayoutPreview = ({ exportWidth, exportHeight, collageInfoList }) => {
  if (!exportWidth || !exportHeight) return null;
  const ratio = exportWidth / exportHeight;
  const paddingTop = (1 / ratio) * 100;
  return (
    <div style={{ marginTop: 6 }}>
      {/* canvas dimensions label */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <span style={{ fontSize: 9, color: "#7986cb", fontWeight: 600, letterSpacing: 0.3 }}>LAYOUT PREVIEW</span>
        <span style={{ fontSize: 9, color: "#9fa8da", fontWeight: 500 }}>{exportWidth} × {exportHeight} px</span>
      </div>

      {/* aspect-ratio container */}
      <div style={{ position: "relative", width: "100%", paddingTop: `${paddingTop}%`, backgroundColor: "#e8eaf6", border: "1.5px solid #9fa8da", borderRadius: 4, overflow: "hidden" }}>
        {/* subtle grid */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)",
          backgroundSize: "10% 10%",
        }} />

        {/* red slot rectangles */}
        {collageInfoList && collageInfoList.map((info, i) => {
          const slotWpx = Math.round(info.getWidth() * exportWidth);
          const slotHpx = Math.round(info.getHeight() * exportHeight);
          const slotLpx = Math.round(info.getLeft() * exportWidth);
          const slotTpx = Math.round(info.getTop() * exportHeight);
          return (
            <div key={i} style={{
              position: "absolute",
              left: `${info.getLeft() * 100}%`, top: `${info.getTop() * 100}%`,
              width: `${info.getWidth() * 100}%`, height: `${info.getHeight() * 100}%`,
              backgroundColor: "rgba(211,47,47,0.15)", border: "2px solid rgba(211,47,47,0.85)",
              borderRadius: 2, boxSizing: "border-box",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: 2,
            }}>
              <span style={{ fontSize: 8, color: "rgba(183,28,28,0.95)", fontWeight: 700, letterSpacing: 0.4, userSelect: "none", lineHeight: 1.2 }}>SLOT {i + 1}</span>
              <span style={{ fontSize: 7, color: "rgba(183,28,28,0.8)", fontWeight: 500, userSelect: "none", lineHeight: 1.2 }}>{slotWpx} × {slotHpx}</span>
              <span style={{ fontSize: 7, color: "rgba(120,20,20,0.6)", fontWeight: 400, userSelect: "none", lineHeight: 1.2 }}>@ {slotLpx}, {slotTpx}</span>
            </div>
          );
        })}
        {(!collageInfoList || collageInfoList.length === 0) && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 10, color: "#9fa8da", fontWeight: 500 }}>No layout data</span>
          </div>
        )}
      </div>

      {/* per-slot dimension rows below */}
      {collageInfoList && collageInfoList.length > 0 && (
        <div style={{ marginTop: 5 }}>
          {collageInfoList.map((info, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 8, color: "#d32f2f", fontWeight: 600 }}>Slot {i + 1}</span>
              <span style={{ fontSize: 8, color: "#555" }}>{Math.round(info.getWidth() * exportWidth)} × {Math.round(info.getHeight() * exportHeight)} px</span>
              <span style={{ fontSize: 8, color: "#888" }}>x:{Math.round(info.getLeft() * exportWidth)} y:{Math.round(info.getTop() * exportHeight)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// Extract sizeAlias from asset fileName: "1_ic_tm_pb_ms_love_..._.png" → "ms"
// Format: {page}_{ic|fl}_tm_{catAlias}_{sizeAlias}_...
const getSizeAliasFromFileName = (fileName) => {
  if (!fileName) return null;
  const parts = fileName.split("_");
  return parts.length >= 5 ? parts[4] : null;
};


// ─────────────────────────────────────────────────────────────────────────────
// StatusChip helper
// ─────────────────────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    1: { label: "Approved", color: "#2e7d32", bg: "#e8f5e9" },
    0: { label: "Pending", color: "#e65100", bg: "#fff3e0" },
    2: { label: "Rejected", color: "#c62828", bg: "#ffebee" },
  };
  const s = map[status] || map[0];
  return (
    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 12, backgroundColor: s.bg, color: s.color, fontWeight: 700, fontSize: 11, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Label / Value row for the detail section
// ─────────────────────────────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <Box display="flex" style={{ marginBottom: 6 }}>
    <Typography variant="body2" style={{ fontWeight: 600, color: "#555", minWidth: 160 }}>{label}:</Typography>
    <Typography variant="body2" style={{ color: "#222" }}>{value || "—"}</Typography>
  </Box>
);


// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const F15ThemeProductsAssetsFormApproval = () => {

  // ── state ──
  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [themesList, setThemesList] = useState([]);     // flat list of pending-approval records
  const [isLoading, setIsLoading] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);   // full record from C15ALLThemesFormView
  const [detailAssets, setDetailAssets] = useState(null);   // assets from C15ALLThemesProductsAssetsView
  const [layoutsList, setLayoutsList] = useState([]);

  // approval action
  const [isApproving, setIsApproving] = useState(false);

  // snackbar / auth
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "" });
  const [isLoginMenu, setIsLoginMenu] = useState(false);


  // ── helpers ──
  const showSnack = (msg, severity = "success") => setSnackbar({ open: true, msg, severity });

  // ── fetch categories list (for filter dropdown) ──
  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    const res = await HttpRequest("GET",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15CommonProductsCategoriesView`,
      null, "authorizeJson");
    if (res.isSuccess) {
      const ALLOWED = [2, 6, 9];
      setCategoriesList((res.data || []).filter(i => ALLOWED.includes(i.id)));
    } else if (res.statusCode === 401) setIsLoginMenu(true);
    setIsLoading(false);
  }, []);

  // ── fetch pending themes list ──
  const fetchThemesList = useCallback(async (categoryId) => {
    setIsListLoading(true);
    const url = categoryId
      ? `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesApprovalList?categoryId=${categoryId}`
      : `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesApprovalList`;
    const res = await HttpRequest("GET", url, null, "authorizeJson");
    if (res.isSuccess) {
      setThemesList(res.data || []);
    } else if (res.statusCode === 401) setIsLoginMenu(true);
    else showSnack(res.message || "Failed to load themes", "error");
    setIsListLoading(false);
  }, []);

  useEffect(() => { fetchCategories(); fetchThemesList(""); }, [fetchCategories, fetchThemesList]);

  const handleCategoryFilter = (e) => {
    const v = e.target.value;
    setSelectedCategoryId(v);
    fetchThemesList(v);
  };

  // ── open approval detail dialog ──
  const handleOpenDetail = async (recordId, productId, categoryId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailRecord(null);
    setDetailAssets(null);

    const [recRes, layoutRes] = await Promise.all([
      HttpRequest("GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesFormView?recordId=${recordId}`,
        null, "authorizeJson"),
      HttpRequest("GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15CommonProductsThemeAssetsLayoutsController?categoryId=${categoryId}`,
        null, "authorizeJson"),
    ]);

    if (recRes.isSuccess && recRes.data && recRes.data.length > 0) {
      setDetailRecord(recRes.data[0]);
    } else if (recRes.statusCode === 401) { setIsLoginMenu(true); setDetailOpen(false); setDetailLoading(false); return; }

    if (layoutRes.isSuccess) setLayoutsList(layoutRes.data || []);

    // fetch assets
    if (productId) {
      const assetsRes = await HttpRequest("GET",
        `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesProductsAssetsView?productId=${productId}`,
        null, "authorizeJson");
      if (assetsRes.isSuccess && assetsRes.data && assetsRes.data.length > 0) {
        setDetailAssets(assetsRes.data[0]);
      }
    }

    setDetailLoading(false);
  };

  // ── approve / reject action ──
  const handleApprovalAction = async (recordId, action) => {
    setIsApproving(true);
    const data = new FormData();
    data.append("recordId", recordId);
    data.append("approvalStatus", action); // 1 = approve, 2 = reject
    const res = await HttpRequest("POST",
      `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C15ALLThemesApprovalUpdate`,
      data, "authorize");
    if (res.isSuccess) {
      showSnack(action == 1 ? "Theme approved successfully" : "Theme rejected", action == 1 ? "success" : "warning");
      setDetailOpen(false);
      fetchThemesList(selectedCategoryId);
    } else if (res.statusCode === 401) setIsLoginMenu(true);
    else showSnack(res.message || "Action failed", "error");
    setIsApproving(false);
  };


  // ── layout overlay helper ──
  // exportW/exportH must be the DESIGN dimensions (e.g. 2737×3000 for square front cover),
  // not the fl image's natural pixel size. Layout methods use hardcoded absolute coordinates
  // (e.g. setLeft(300/exportWidth)) that are calibrated for design dims — passing a different
  // resolution causes the normalized fractions to be wrong and slots to overflow the canvas.
  const getCollageOverlay = (layoutNo, sizeAlias, exportW, exportH) => {
    if (!layoutNo || !sizeAlias || !exportW || !exportH) return null;
    const fnName = `${sizeAlias}_${(layoutNo).replace(/\s+/g, "")}`;
    if (typeof LayoutMethods[fnName] !== "function") {
      console.warn("[Approval] LayoutMethods has no method:", fnName);
      return null;
    }
    // isRightPage always false: the addition in layout methods is designed for the full
    // double-spread export canvas. Each page is previewed individually at single-page dims.
    const collageInfoList = LayoutMethods[fnName]({ exportWidth: exportW, exportHeight: exportH, isRightPage: false });
    return { collageInfoList, exportWidth: exportW, exportHeight: exportH };
  };



  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <LoginMenu reload open={isLoginMenu} handleClose={() => setIsLoginMenu(false)} />
      <CustomizedSnackbar
        isOpen={snackbar.open}
        message={snackbar.msg}
        severity={snackbar.severity}
        handleCloseSnackbar={() => setSnackbar(s => ({ ...s, open: false }))}
        autoHideDuration={3000}
      />

      <Grid container xs={12} direction="column" justifyContent="center" alignItems="center" style={{ padding: 10 }}>
        <Toolbar>
          <NavBar isAuthorize title="Theme Assets Approval" />
        </Toolbar>

        <Card style={{ width: "98%", paddingBottom: 100, paddingLeft: 20, paddingRight: 20 }}>
          <CardContent>

            {/* ── Header ── */}
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 8 }}>
              <Typography variant="h5" color="primary" style={{ fontWeight: 700 }}>
                Theme Products — Approval Queue
              </Typography>
              <Tooltip title="Refresh list">
                <IconButton size="small" onClick={() => fetchThemesList(selectedCategoryId)}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider style={{ marginBottom: 20 }} />

            {/* ── Filter ── */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select fullWidth label="Filter by Category" variant="outlined" size="small"
                  value={selectedCategoryId}
                  onChange={handleCategoryFilter}
                  disabled={isLoading}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categoriesList.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.label || c.userLabel}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* ── List ── */}
            {isListLoading ? (
              <Box display="flex" justifyContent="center" style={{ padding: 40 }}>
                <CircularProgress />
              </Box>
            ) : themesList.length === 0 ? (
              <Box style={{ padding: 40, textAlign: "center" }}>
                <Typography variant="body1" style={{ color: "#888" }}>No theme records found.</Typography>
              </Box>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f5f5f5" }}>
                      {["#", "Theme Label", "Category", "Theme Category", "Size", "Total Pages", "Product Types", "Status", "Action"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "#444", borderBottom: "2px solid #e0e0e0", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {themesList.map((row, idx) => (
                      <tr key={row.recordId || idx} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                        <td style={{ padding: "7px 12px", color: "#888" }}>{idx + 1}</td>
                        <td style={{ padding: "7px 12px", fontWeight: 600 }}>{row.themeLabel}</td>
                        <td style={{ padding: "7px 12px" }}>{row.categoryLabel || row.categoryId}</td>
                        <td style={{ padding: "7px 12px" }}>{row.themeCategoryLabel || row.themeCategoryId}</td>
                        <td style={{ padding: "7px 12px" }}>{row.sizeLabel || row.sizeId}</td>
                        <td style={{ padding: "7px 12px" }}>{row.totalPages}</td>
                        <td style={{ padding: "7px 12px" }}>
                          {Array.isArray(row.productTypes)
                            ? row.productTypes.map(pt => (
                              <Chip key={pt.id} label={pt.label} size="small" style={{ marginRight: 4, marginBottom: 2, fontSize: 10 }} />
                            ))
                            : row.typeLabel || "—"}
                        </td>
                        <td style={{ padding: "7px 12px" }}>
                          <StatusChip status={row.approvalStatus} />
                        </td>
                        <td style={{ padding: "7px 12px" }}>
                          <Button
                            variant="contained"
                            size="small"
                            disabled={row.approvalStatus !== 0}
                            style={{
                              backgroundColor: row.approvalStatus === 0 ? "#1565c0" : "#e0e0e0",
                              color: row.approvalStatus === 0 ? "#fff" : "#9e9e9e",
                              textTransform: "none", fontWeight: 600, fontSize: 11,
                            }}
                            onClick={() => handleOpenDetail(row.recordId, row.productId, row.categoryId)}
                          >
                            {row.approvalStatus === 0 ? "Review" : row.approvalStatus === 1 ? "Approved" : "Rejected"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* ── Detail / Approval Dialog ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ style: { borderRadius: 12, maxHeight: "95vh" } }}>
        <DialogTitle style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" style={{ fontWeight: 700, color: "#333" }}>
              Theme Detail — Approval Review
            </Typography>
            <IconButton size="small" onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers style={{ padding: 24 }}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" style={{ padding: 60 }}>
              <CircularProgress />
            </Box>
          ) : detailRecord ? (
            <DetailContent
              record={detailRecord}
              assets={detailAssets}
              layoutsList={layoutsList}
              getCollageOverlay={getCollageOverlay}
            />
          ) : (
            <Typography variant="body2" style={{ color: "#888" }}>Failed to load record.</Typography>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", gap: 8 }}>
          <Button onClick={() => setDetailOpen(false)} disabled={isApproving}
            style={{ textTransform: "none", color: "#666" }}>
            Close
          </Button>
          {detailRecord && (
            <>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                disabled={isApproving}
                onClick={() => handleApprovalAction(detailRecord.recordId, 2)}
                style={{ textTransform: "none", color: "#c62828", borderColor: "#c62828", fontWeight: 600 }}
              >
                {isApproving ? <CircularProgress size={16} /> : "Reject"}
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                disabled={isApproving}
                onClick={() => handleApprovalAction(detailRecord.recordId, 1)}
                style={{ textTransform: "none", backgroundColor: "#2e7d32", color: "#fff", fontWeight: 600 }}
              >
                {isApproving ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Approve"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

    </>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// AssetSlotCard — single image column with dimensions reported via LazyImage
// ─────────────────────────────────────────────────────────────────────────────
const AssetSlotCard = ({ slot, slotLabel, accentColor, onDimensions }) => {
  const [dims, setDims] = useState(null);
  if (!slot) return (
    <Box style={{ border: "1px dashed #e0e0e0", borderRadius: 6, padding: 10, backgroundColor: "#fafafa", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography variant="caption" style={{ color: "#bbb" }}>No asset</Typography>
    </Box>
  );
  const imgSrc = slot.fileUrl ? getImageUrl(slot.fileUrl) : null;
  return (
    <Box style={{ border: `1px solid ${accentColor}22`, borderRadius: 6, padding: 10, backgroundColor: "#fff", height: "100%" }}>
      {/* header */}
      <Typography variant="caption" style={{ fontWeight: 700, color: accentColor, display: "block", marginBottom: 6 }}>
        {slotLabel}
      </Typography>

      {/* image */}
      {imgSrc && (
        <Box style={{ marginBottom: 8 }}>
          <LazyImage
            src={imgSrc}
            alt={slotLabel}
            aspectRatio={1}
            onDimensions={(w, h) => {
              setDims({ w, h });
              if (onDimensions) onDimensions(w, h);
            }}
          />
        </Box>
      )}

      {/* dimensions */}
      <Typography variant="caption" style={{ display: "block", color: dims ? "#2e7d32" : "#aaa", fontWeight: 600, marginBottom: 3 }}>
        {dims ? `📐 ${dims.w} × ${dims.h} px` : "📐 loading…"}
      </Typography>

      {/* filename */}
      <Typography variant="caption" style={{ color: "#555", display: "block", wordBreak: "break-all", fontSize: 10 }}>
        {slot.fileName || "—"}
      </Typography>
    </Box>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// DetailContent — all descriptive info + assets grid
// ─────────────────────────────────────────────────────────────────────────────
// Map category + sizeId + page-type → design export dimensions (must match layout method calibration)
const getExportDims = (categoryId, sizeId, isFrontCover, isBackCover) => {
  const id = parseInt(categoryId);
  let w = 3508, h = 2400;
  if (id === 2) {
    if (sizeId == 2) {       // Landscape photobook
      if (isFrontCover)       { w = 3796; h = 3000; }
      else if (isBackCover)   { w = 4010; h = 3000; }
      else                    { w = 3508; h = 2400; }
    } else if (sizeId == 4) { // Square photobook
      if (isFrontCover)       { w = 2737; h = 3000; }
      else if (isBackCover)   { w = 2961; h = 3000; }
      else                    { w = 2400; h = 2400; }
    }
  } else if (id === 6) {
    w = 3600; h = sizeId == 15 ? 3600 : 5400;
  } else if (id === 9) {
    w = 2550; h = sizeId == 23 ? 1125 : 1050;
  }
  return { w, h };
};

const DetailContent = ({ record, assets, layoutsList, getCollageOverlay }) => {
  const r = record;

  // Actual JSON shape from C15ALLThemesProductsAssetsView:
  // assets = { assetsTypes: [ { assetTypeId:1, assetTypeLabel:"App Assets", assets:[...] }, { assetTypeId:2, ... } ] }
  // Each asset: { id, fileName, fileUrl, layoutNo, themeLayoutId, path }
  const assetsTypes = assets?.assetsTypes || [];
  const appAssets   = (assetsTypes.find(t => t.assetTypeId == 1)?.assets) || [];
  const printAssets = (assetsTypes.find(t => t.assetTypeId == 2)?.assets) || [];

  // page number is the leading digits in fileName e.g. "3_ic_..." → 3
  const getPageNo = (asset, fallback) => {
    if (!asset?.fileName) return fallback;
    const m = asset.fileName.match(/^(\d+)_/);
    return m ? parseInt(m[1]) : fallback;
  };

  const totalSlots = Math.max(appAssets.length, printAssets.length);
  // derive actual last page number from filenames — more reliable than r.totalPages
  // which may store the user-selected count (e.g. 10) excluding covers (actual total: 12)
  const allPageNos = [...appAssets, ...printAssets].map(a => getPageNo(a, 0)).filter(n => n > 0);
  const maxPageNo = allPageNos.length > 0 ? Math.max(...allPageNos) : totalSlots;

  return (
    <div>

      {/* ── Section 1: Core Details ── */}
      <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#1565c0", marginBottom: 12 }}>
        Core Details
      </Typography>
      <Grid container spacing={0}>
        <Grid item xs={12} sm={6}>
          <DetailRow label="Record ID" value={r.recordId} />
          <DetailRow label="Theme Label" value={r.themeLabel} />
          <DetailRow label="Category" value={r.categoryLabel || r.categoryId} />
          <DetailRow label="Theme Category" value={r.themeCategoryLabel || r.themeCategoryId} />
          <DetailRow label="Size" value={r.sizeLabel || r.sizeId} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DetailRow label="Total Pages" value={r.totalPages} />
          <DetailRow label="Description" value={r.description} />
          <DetailRow label="Approval Status" value={<StatusChip status={r.approvalStatus} />} />
          <DetailRow label="Product Types" value={
            r.typeLabel || "—"
          } />
        </Grid>
      </Grid>

      <Divider style={{ margin: "16px 0" }} />

      {/* ── Section 2: Preview Image ── */}
      {(r.previewFile || r.previewImageUrl) && (
        <>
          <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#1565c0", marginBottom: 12 }}>
            Preview Image
          </Typography>
          <Box style={{ marginBottom: 16, maxWidth: 280 }}>
            <LazyImage
              src={getImageUrl(r.path ? `${r.path}/${r.previewFile}` : (r.previewImageUrl || r.previewFile))}
              alt="preview"
              aspectRatio={1}
            />
            {r.previewFile && (
              <Typography variant="caption" style={{ display: "block", color: "#888", marginTop: 4 }}>
                {r.previewFile}
              </Typography>
            )}
          </Box>
          <Divider style={{ margin: "16px 0" }} />
        </>
      )}

      {/* ── Section 3: Template Assets ── */}
      <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#1565c0", marginBottom: 12 }}>
        Template Assets
      </Typography>

      {totalSlots === 0 ? (
        <Typography variant="body2" style={{ color: "#888" }}>No assets uploaded yet.</Typography>
      ) : (
        Array.from({ length: totalSlots }).map((_, i) => {
          const appAsset   = appAssets[i];
          const printAsset = printAssets[i];
          const pageNo = getPageNo(appAsset, null) || getPageNo(printAsset, null) || (i + 1);

          const refAsset = appAsset || printAsset;
          const sizeAlias = getSizeAliasFromFileName(refAsset?.fileName);
          const isFrontCover = pageNo === 1;
          const isBackCover = pageNo === maxPageNo && maxPageNo > 1;
          const { w: exportW, h: exportH } = getExportDims(r.categoryId, r.sizeId, isFrontCover, isBackCover);
          const overlay = getCollageOverlay(refAsset?.layoutNo, sizeAlias, exportW, exportH);

          return (
            <Box key={i} style={{
              border: "1px solid #e0e0e0", borderRadius: 8, padding: 14,
              marginBottom: 16, backgroundColor: "#fafafa",
            }}>
              {/* Page header */}
              <Box display="flex" alignItems="center" style={{ marginBottom: 10 }}>
                <Typography variant="body2" style={{ fontWeight: 700, color: "#1565c0", marginRight: 8 }}>
                  Page {pageNo}
                </Typography>
                {refAsset?.layoutNo && (
                  <span style={{ fontSize: 11, color: "#7986cb", fontWeight: 600, backgroundColor: "#e8eaf6", padding: "2px 8px", borderRadius: 10 }}>
                    Layout: {refAsset.layoutNo}
                  </span>
                )}
              </Box>

              {/* 3-column: ic | fl | layout preview */}
              <Grid container spacing={2}>

                {/* ── Column 1: App Asset (ic) ── */}
                <Grid item xs={12} sm={4}>
                  <AssetSlotCard
                    slot={appAsset}
                    slotLabel="App Asset (ic)"
                    accentColor="#1565c0"
                  />
                </Grid>

                {/* ── Column 2: Export Asset (fl) ── */}
                <Grid item xs={12} sm={4}>
                  <AssetSlotCard
                    slot={printAsset}
                    slotLabel="Export Asset (fl)"
                    accentColor="#6a1b9a"
                  />
                </Grid>

                {/* ── Column 3: Layout Preview (canvas dims from fl natural size) ── */}
                <Grid item xs={12} sm={4}>
                  <Box style={{ border: "1px solid #e8eaf6", borderRadius: 6, padding: 10, backgroundColor: "#fff", height: "100%" }}>
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#5c6bc0", display: "block", marginBottom: 4 }}>
                      Layout Shape
                    </Typography>
                    {overlay ? (
                      <LayoutPreview
                        exportWidth={overlay.exportWidth}
                        exportHeight={overlay.exportHeight}
                        collageInfoList={overlay.collageInfoList}
                      />
                    ) : (
                      <Typography variant="caption" style={{ color: "#aaa" }}>
                        Layout &quot;{refAsset?.layoutNo}&quot; not available
                      </Typography>
                    )}
                  </Box>
                </Grid>

              </Grid>
            </Box>
          );
        })
      )}
    </div>
  );
};


export default F15ThemeProductsAssetsFormApproval;
